import { Box } from "@mui/material";
import { Children, useEffect, useState, ReactNode } from "react";

export interface Props {
  children: typeof Children | ReactNode;
}

/**
 * The css text-overflow: ellipsis property does not work well (at all)
 * for complex inline text elements. This somewhat emulates the behavior
 * by showing a '...' continuation when the children overflow, and adding
 * a gradient mask, to soften the unavoidably displayed partially overflowed
 * children.
 */
export default function FakeEllipsisOverflow({ children }: Props) {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [el, setEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!el) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // there should only ever be one entry (the current cell)
        const _el = entry.target;
        const scrollWidth = _el.scrollWidth;
        const clientWidth = _el.clientWidth;

        const scrollHeight = _el.scrollHeight;
        const clientHeight = _el.clientHeight;

        setIsOverflowed(
          scrollWidth > clientWidth || scrollHeight > clientHeight
        );
      }
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [el]);

  return (
    <Box
      position="relative"
      sx={{
        "&::after": {
          content: isOverflowed ? '"..."' : '""',
          position: "absolute",
          top: "0.2em",
          right: 0,
          transform: "translateX(-100%)",
        },
      }}
    >
      <Box
        ref={(boxEl: HTMLDivElement | null) => {
          setEl(boxEl);
        }}
        style={{
          maskImage: isOverflowed
            ? // fade out the right-hand side of the container to make room for the '...'
              "linear-gradient(to right, black calc(100% - 4em), transparent calc(100% - 2em))"
            : undefined,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
